import type { AuditEntry, Paginated } from "@lucky-try/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/form";
import {
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from "../components/ui/misc";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";

export function AuditPage() {
  const search = useSearch({ from: "/authed/audit" });
  const navigate = useNavigate({ from: "/audit" });
  const [action, setAction] = useState(search.action ?? "");
  const [entityType, setEntityType] = useState(search.entityType ?? "");

  const entries = useQuery({
    queryKey: ["audit", search],
    queryFn: () =>
      api<Paginated<AuditEntry>>("/admin/audit", { query: { ...search, pageSize: 50 } }),
  });

  if (entries.error)
    return <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />;

  return (
    <>
      <PageHeader title="Audit log" description="Every staff mutation, newest first." />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (previous) => ({
                ...previous,
                page: 1,
                action: action || undefined,
                entityType: entityType || undefined,
              }),
            });
          }}
        >
          <Input
            placeholder="Action, e.g. game.update"
            value={action}
            onChange={(event) => setAction(event.target.value)}
            className="w-52"
          />
          <Input
            placeholder="Entity type, e.g. game"
            value={entityType}
            onChange={(event) => setEntityType(event.target.value)}
            className="w-44"
          />
          <Button type="submit">Filter</Button>
        </form>
      </Toolbar>

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        {entries.isPending ? (
          <Loading />
        ) : entries.data.items.length === 0 ? (
          <EmptyState>No entries.</EmptyState>
        ) : (
          <table className="text-ui w-full border-collapse text-left">
            <thead className="bg-well">
              <tr className="border-line border-b">
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">When</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Actor</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Action</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Entity</th>
                <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Data</th>
              </tr>
            </thead>
            <tbody>
              {entries.data.items.map((entry) => (
                <tr
                  key={entry.id}
                  className="t-row border-line/60 border-b align-top last:border-0"
                >
                  <td className="text-muted text-caption h-9 px-3 whitespace-nowrap">
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td className="text-body text-caption px-3">
                    {entry.actorName ?? entry.actorType}
                  </td>
                  <td className="text-ink px-3 font-medium">{entry.action}</td>
                  <td className="text-muted text-caption px-3">
                    {entry.entityType} #{entry.entityId}
                  </td>
                  <td className="max-w-md px-3">
                    {entry.data && (
                      <code
                        className="text-muted text-caption block truncate font-mono"
                        title={JSON.stringify(entry.data)}
                      >
                        {JSON.stringify(entry.data)}
                      </code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={entries.data?.page ?? 1}
          pageSize={entries.data?.pageSize ?? 50}
          total={entries.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>
    </>
  );
}
